import { Component, h, Prop, Host, Element, State } from '@stencil/core';

const COMPONENT_NAME = 'sn-how-to-decoration-type-1';

export type HowToItem = {
  question?: string;
  answer?: string;
  raw_html?: string;
  mediaId?: string;
};

/**
 * SignNow how-to section with step-by-step carousel content and paired document-signing illustrations.
 * When item data is provided, renders its own section wrapper, heading, subtitle, slide content,
 * navigation dots, and media. When only `media-id` is provided, renders a single decoration image
 * set for use inside another component.
 *
 * @product signnow
 * @category SignNow
 * @slot item - {multiple}, <div data-question="..." data-answer="..." data-media-id="1|2|3|4|5|6"> - How-to carousel items
 * @example
 *   <sn-how-to-decoration-type-1
 *     title="How to Sign a PDF Online"
 *     subtitle="Your entire business wins with electronic signature software."
 *   >
 *     <div slot="item" data-question="Upload a document" data-answer="&lt;p&gt;Add your file to airSlate SignNow.&lt;/p&gt;" data-media-id="1"></div>
 *     <div slot="item" data-question="Create your eSignature" data-answer="&lt;p&gt;Draw, type, or upload your signature.&lt;/p&gt;" data-media-id="2"></div>
 *   </sn-how-to-decoration-type-1>
 */
@Component({
  tag: 'sn-how-to-decoration-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnHowToDecoration {
  @Element() el: HTMLElement;

  @State() currentSlide: number = 0;
  @State() parsedItems: HowToItem[] = [];

  /**
   * Color mode for SignNow theming.
   * @default "signnow"
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * The media ID to display. One of: screen01, screen02, screen03, screen04, screen05, screen06.
   * @default screen01
   */
  @Prop({ attribute: 'media-id' }) mediaId: string = 'screen01';

  /**
   * Section heading text.
   * @content
   */
  @Prop({ attribute: 'title' }) heading?: string;

  /**
   * Section subtitle text.
   * @content
   */
  @Prop() subtitle?: string;

  /**
   * Array of how-to carousel items.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} question - Slide heading text
   * @item.property {string} answer - Slide body HTML
   * @item.property {string} raw_html - Alternative slide body HTML
   * @item.property {string} mediaId - Illustration id, 1 through 6 or screen01 through screen06
   * @item.required question
   * @item.required answer
   * @item.required mediaId
   * @item.content question answer raw_html
   * @minItems 1
   */
  @Prop() items?: HowToItem[] | string;

  componentWillLoad() {
    this.parsedItems = this.getItems();
  }

  private decodeHtmlEntities(value: string): string {
    return value
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  private parseItemsAttribute(value: string): HowToItem[] {
    try {
      const parsedItems = JSON.parse(this.decodeHtmlEntities(value));
      return Array.isArray(parsedItems) ? parsedItems : [];
    } catch {
      return [];
    }
  }

  private getItems(): HowToItem[] {
    if (Array.isArray(this.items)) {
      return this.items;
    }

    if (typeof this.items === 'string') {
      return this.parseItemsAttribute(this.items);
    }

    const itemsAttribute = this.el.getAttribute('items');
    if (itemsAttribute) {
      return this.parseItemsAttribute(itemsAttribute);
    }

    return Array.from(this.el.querySelectorAll<HTMLElement>('[slot="item"]')).map(item => ({
      question: item.dataset.question || item.getAttribute('dataQuestion') || item.getAttribute('dataquestion') || undefined,
      answer: item.dataset.answer || item.getAttribute('dataAnswer') || item.getAttribute('dataanswer') || undefined,
      mediaId: item.dataset.mediaId || item.getAttribute('dataMediaId') || item.getAttribute('datamediaid') || undefined,
    }));
  }

  private getMediaId(mediaId?: string): string {
    if (!mediaId) {
      return this.mediaId;
    }

    return mediaId.startsWith('screen') ? mediaId : `screen${String(mediaId).padStart(2, '0')}`;
  }

  private goToSlide(index: number) {
    if (index < 0 || index >= this.parsedItems.length) {
      return;
    }

    this.currentSlide = index;
  }

  private nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.parsedItems.length;
    this.goToSlide(nextIndex);
  }

  private prevSlide() {
    const prevIndex = (this.currentSlide - 1 + this.parsedItems.length) % this.parsedItems.length;
    this.goToSlide(prevIndex);
  }

  private renderScreen01() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen01" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-1@2x.webp 2x"
            alt="How to Sign a PDF Online"
            aspectRatio="525/350"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-01-2@2x.webp 2x"
            alt="How to Sign a PDF Online"
            aspectRatio="320/220"
          />
        </div>
      </div>
    );
  }

  private renderScreen02() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen02" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-1@2x.webp 2x"
            alt="How to Sign a PDF Using Google Chrome"
            aspectRatio="525/350"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-02-2@2x.webp 2x"
            alt="How to Sign a PDF Using Google Chrome"
            aspectRatio="255/212"
          />
        </div>
      </div>
    );
  }

  private renderScreen03() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen03" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-1@2x.webp 2x"
            alt="How to Sign a PDF in Gmail"
            aspectRatio="525/350"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-2@2x.webp 2x"
            alt="How to Sign a PDF in Gmail"
            aspectRatio="1/1"
          />
          <me-picture
            class="sign-illustration__button"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-03-3@2x.webp 2x"
            alt="How to Sign a PDF in Gmail"
            aspectRatio="156/54"
          />
        </div>
      </div>
    );
  }

  private renderScreen04() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen04" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-1@2x.webp 2x"
            alt="How to Sign a PDF on a Mobile Device"
            aspectRatio="210/442"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-2@2x.webp 2x"
            alt="How to Sign a PDF on a Mobile Device"
            aspectRatio="164/220"
          />
          <me-picture
            class="sign-illustration__image-tertiary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-04-3@2x.webp 2x"
            alt="How to Sign a PDF on a Mobile Device"
            aspectRatio="164/220"
          />
        </div>
      </div>
    );
  }

  private renderScreen05() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen05" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1.png 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1.webp 1x, https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-1@2x.webp 2x"
            alt="How to Sign a PDF on iPhone"
            aspectRatio="262/427"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-2.png"
            srcSet="https://marketing-static.signnow.com/349/images/home/how-to-sign/new/screen-05-2.png 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-05-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-05-2.webp 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-05-2@2x.webp 2x"
            alt="How to Sign a PDF on iPhone"
            aspectRatio="262/427"
          />
        </div>
      </div>
    );
  }

  private renderScreen06() {
    return (
      <div class="page-section__column page-section__column--media">
        <div class="sign-illustration sign-illustration--screen06" style={{ display: 'block' }}>
          <div class="sign-illustration__square"></div>
          <me-picture
            class="sign-illustration__image-primary"
            src="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1.png"
            srcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1.png 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1.webp 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-1@2x.webp 2x"
            alt="How to Sign a PDF on Android"
            aspectRatio="261/460"
          />
          <me-picture
            class="sign-illustration__image-secondary"
            src="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2.png"
            srcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2.png 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2@2x.png 2x"
            webpSrcSet="https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2.webp 1x, https://marketing-static.signnow.com/341/images/home/how-to-sign/new/screen-06-2@2x.webp 2x"
            alt="How to Sign a PDF on Android"
            aspectRatio="261/460"
          />
        </div>
      </div>
    );
  }

  render() {
    const items = this.parsedItems;
    if (items.length > 0) {
      const currentItem = items[this.currentSlide] || items[0];
      const currentMediaId = this.getMediaId(currentItem.mediaId);

      return (
        <Host data-color-mode={this.colorMode}>
          <me-section class={COMPONENT_NAME} yGap="2xl" yGapLg="5xl" role="region" aria-label={this.heading || 'How-to decoration'}>
            <me-row yGap="4xl" yGapLg="6xl">
              <me-column span="12" class={`${COMPONENT_NAME}__intro`}>
                {this.heading && (
                  <me-heading variant="2" align="center" tag="h2">
                    {this.heading}
                  </me-heading>
                )}
                {this.subtitle && (
                  <me-text variant="medium" align="center">
                    {this.subtitle}
                  </me-text>
                )}
              </me-column>
              <me-column span="12">
                <me-row align="center" class={`${COMPONENT_NAME}__slide`}>
                  <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__media`}>
                    {this.renderMedia(currentMediaId)}
                  </me-column>
                  <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__content`}>
                    {currentItem.question && (
                      <me-heading variant="5" class={`${COMPONENT_NAME}__heading`} tag="h3">
                        {currentItem.question}
                      </me-heading>
                    )}
                    {(currentItem.raw_html || currentItem.answer) && <div class={`${COMPONENT_NAME}__text`} innerHTML={currentItem.raw_html || currentItem.answer}></div>}
                  </me-column>
                </me-row>
                {items.length > 1 && (
                  <div class={`${COMPONENT_NAME}__navigation`}>
                    <div class={`${COMPONENT_NAME}__dots`} role="tablist" aria-label="How-to steps">
                      {items.map((item, index) => (
                        <button
                          key={index}
                          type="button"
                          role="tab"
                          aria-selected={index === this.currentSlide ? 'true' : 'false'}
                          aria-label={`Show step ${index + 1}${item.question ? `: ${item.question}` : ''}`}
                          class={`${COMPONENT_NAME}__dot ${index === this.currentSlide ? `${COMPONENT_NAME}__dot--active` : ''}`}
                          onClick={() => this.goToSlide(index)}
                        />
                      ))}
                    </div>
                    <div class={`${COMPONENT_NAME}__arrows`}>
                      <me-button
                        tag="button"
                        variant="ghost"
                        size="sm"
                        ariaLabel="Previous step"
                        icon="arrows--chevron-left"
                        iconPosition="only"
                        onClick={() => this.prevSlide()}
                        class={`${COMPONENT_NAME}__arrow ${COMPONENT_NAME}__arrow--prev`}
                      />
                      <me-button
                        tag="button"
                        variant="ghost"
                        size="sm"
                        ariaLabel="Next step"
                        icon="arrows--chevron-right"
                        iconPosition="only"
                        onClick={() => this.nextSlide()}
                        class={`${COMPONENT_NAME}__arrow ${COMPONENT_NAME}__arrow--next`}
                      />
                    </div>
                  </div>
                )}
              </me-column>
            </me-row>
          </me-section>
        </Host>
      );
    }

    return <Host data-color-mode={this.colorMode}>{this.renderMedia(this.mediaId)}</Host>;
  }

  private renderMedia(mediaId: string) {
    let content: any = null;
    switch (mediaId) {
      case 'screen01':
        content = this.renderScreen01();
        break;
      case 'screen02':
        content = this.renderScreen02();
        break;
      case 'screen03':
        content = this.renderScreen03();
        break;
      case 'screen04':
        content = this.renderScreen04();
        break;
      case 'screen05':
        content = this.renderScreen05();
        break;
      case 'screen06':
        content = this.renderScreen06();
        break;
    }

    return content;
  }
}
